// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Areas: AgentsView (agents.*) + AgentEditorView (agentEditor.*).
import type { LocaleStrings } from './base';

export const agentsStrings: LocaleStrings = {
  en: {
    // ── web-ui AgentsView.vue ──
    'agents.title':                  'Agents',
    'agents.subtitle':               'The agents reachable via @hq. Bare @hq goes to your default; @hq <name> routes to that agent. Each one has its own skills, integrations, and memory.',
    'agents.add':                    'Add agent',

    'agents.nudge.title':            '{name} is still on the platform default.',
    'agents.nudge.sub':              "Tailor the agent to your business so replies match what your team actually works on. We'll draft from your business profile + workspace channel activity - you review and save.",
    'agents.nudge.cta':              'Tailor {name}',

    'agents.empty.title':            'No agents installed yet',
    'agents.empty.body':             'Agents are the personas reachable via @hq, each with its own skills, integrations, and memory. Install your first one to get started.',

    'agents.default':                'Default',
    'agents.default_tip':            'Default agent - receives bare @hq messages',

    'agents.meta.runtime':           'Runtime',
    'agents.meta.model':             'Model',
    'agents.meta.model_default':     'default',

    'agents.action.set_default':     'Set as default',
    'agents.action.disable':         'Disable',
    'agents.action.enable':          'Enable',

    'agents.set_default.title':      'Set {name} as default?',
    'agents.set_default.body':       'Messages to `@hq` without a persona token will route to {name}. Only one agent can be the default at a time.',
    'agents.set_default.confirm':    'Set as default',
    'agents.set_default.success':    '{name} is now your default agent',
    'agents.set_default.error':      'Set-default failed',

    'agents.disable.title':          'Disable {name}?',
    'agents.disable.body':           "New `@hq {slug}` messages will fail to route. Existing conversations bound to this agent are preserved but won't accept new turns.",
    'agents.disable.confirm':        'Disable',
    'agents.disable.success':        'Disabled {name}',
    'agents.disable.error':          'Disable failed',

    'agents.enable.title':           'Enable {name}?',
    'agents.enable.body':            "{name} will route again via `@hq` and the web. Its email address stays released (a disabled agent's address can't be re-issued) until you rename the agent.",
    'agents.enable.confirm':         'Enable',
    'agents.enable.success':         'Enabled {name}',
    'agents.enable.error':           'Enable failed',

    // ── web-ui AgentEditorView.vue ──
    'agentEditor.title_edit':        'Edit {name}',
    'agentEditor.title_new':         'New agent',
    'agentEditor.agent_noun':        'agent',
    'agentEditor.sections_aria':     'Editor sections',
    'agentEditor.adv_sections_aria': 'Advanced sections',

    'agentEditor.tab.identity':      'Identity & routing',
    'agentEditor.tab.behavior':      'Behavior',
    'agentEditor.tab.advanced':      'Advanced',
    'agentEditor.tab.skills':        'Skills',
    'agentEditor.tab.integrations':  'Integrations',

    'agentEditor.noun.skill':        'skill',
    'agentEditor.noun.integration':  'integration',

    'agentEditor.identity.heading':  'Identity',
    'agentEditor.identity.lede':     'How this agent is named and shown to your team.',
    'agentEditor.routing.heading':   'Routing',
    'agentEditor.routing.lede':      'The @hq handles people use to reach this agent in Slack.',
    'agentEditor.behavior.heading':  'Behavior',
    'agentEditor.behavior.lede':     'What this agent does, and how it talks.',

    'agentEditor.field.slug':         'Slug',
    'agentEditor.field.display_name': 'Display name',
    'agentEditor.field.avatar':       'Avatar',
    'agentEditor.field.aliases':      'Aliases',
    'agentEditor.field.instructions': 'Instructions',
    'agentEditor.field.tone':         'Tone',
    'agentEditor.field.verbosity':    'Verbosity',
    'agentEditor.field.runtime':      'Runtime',
    'agentEditor.field.model':        'Model',
    'agentEditor.model_locked':       'Contact support to activate',
    'agentEditor.field.cautions':     'Cautions',
    'agentEditor.field.languages':    'Languages',

    'agentEditor.hint.slug':          'Routing handle (a-z, 0-9, hyphen)',
    'agentEditor.hint.slug_edit':     'Routing handle (a-z, 0-9, hyphen); rename keeps the old slug as an alias',
    'agentEditor.hint.display_name':  'Shown in the UI',
    'agentEditor.hint.description':   'Short blurb shown in the UI',
    'agentEditor.hint.avatar':        "Shown next to this agent's messages. Upload your own or generate one.",
    'agentEditor.hint.aliases':       'Extra handles besides the slug (press Enter to add)',
    'agentEditor.hint.instructions':  'What this agent does, scope, tone. Empty = generic helper.',
    'agentEditor.hint.tone':          'How this agent talks; leave unset for the platform default',
    'agentEditor.hint.verbosity':     'How long replies should run',
    'agentEditor.hint.runtime':       'Which agent SDK powers this agent',
    'agentEditor.hint.model':         'Which model this agent runs on; cost scales with the choice',
    'agentEditor.hint.cautions':      'Short topics this agent should avoid (press Enter to add)',
    'agentEditor.hint.languages':     'The first is the default; the rest are languages the agent may also answer in. Click a chip to make it the default.',

    'agentEditor.ph.slug':            'anna',
    'agentEditor.ph.display_name':    'Anna',
    'agentEditor.ph.description':     'CFO - financial reports, Stripe queries, expense analysis',
    'agentEditor.ph.add_alias':       'add alias',
    'agentEditor.ph.instructions':    'e.g. You are Anna, the CFO at this workspace. Your scope: financial reports, expenses, P&L, Stripe queries. When asked about non-finance topics, suggest the user ask Max or another agent. Be precise with numbers and always cite the source query.',
    'agentEditor.ph.caution':         'e.g. salary specifics',

    'agentEditor.tone.formal':        'Formal',
    'agentEditor.tone.friendly':      'Friendly',
    'agentEditor.tone.direct':        'Direct',
    'agentEditor.tone.casual':        'Casual',

    'agentEditor.verbosity.concise':  'Concise',
    'agentEditor.verbosity.balanced': 'Balanced',
    'agentEditor.verbosity.detailed': 'Detailed',

    'agentEditor.runtime_note':       "Changing the runtime re-provisions this agent's sandbox and resets the model to the new runtime's default.",

    'agentEditor.avatar.uploading':       'Uploading…',
    'agentEditor.avatar.replace':         'Replace…',
    'agentEditor.avatar.upload':          'Upload…',
    'agentEditor.avatar.generating':      'Generating…',
    'agentEditor.avatar.generate':        'Generate',
    'agentEditor.avatar.staged_gen_hint': 'Will be generated when you create the agent.',
    'agentEditor.avatar.staged_hint':     'Staged - applied when you create the agent.',
    'agentEditor.avatar.updated':         'Updated avatar for {name}',
    'agentEditor.avatar.upload_failed':   'Avatar upload failed',
    'agentEditor.avatar.generated':       'Generated a new avatar.',
    'agentEditor.avatar.generate_failed': 'Avatar generation failed',
    'agentEditor.avatar.staged_gen':      'Avatar will be generated when you create the agent.',

    'agentEditor.tailor.tip':           'Ask Claude to draft a tailored prompt using your business profile + workspace channel activity. You review + save.',
    'agentEditor.tailor.drafting':      'Drafting…',
    'agentEditor.tailor.cta':           'Tailor to this workspace',
    'agentEditor.tailor.confirm_title': 'Replace the current Instructions?',
    'agentEditor.tailor.confirm_body':  'Claude will draft a new prompt from your business profile + workspace channel activity. Your current edits will be discarded.',
    'agentEditor.tailor.confirm_cta':   'Draft new',
    'agentEditor.tailor.success':       'Drafted a tailored prompt - review and save when ready.',
    'agentEditor.tailor.error':         'Tailor request failed',

    'agentEditor.block.slug':         'Add a slug on Identity & routing to save.',
    'agentEditor.block.display_name': 'Add a display name on Identity & routing to save.',

    'agentEditor.save.save_changes':       'Save changes',
    'agentEditor.save.create':             'Create agent',
    'agentEditor.save.generating_avatar':  'Generating avatar…',
    'agentEditor.save.created':            'Created agent {slug}',
    'agentEditor.save.updated':            'Updated agent {slug}',
    'agentEditor.save.failed':             'Save failed',
    'agentEditor.save.avatar_apply_failed':'Agent created, but the avatar could not be applied. Set it from the editor.',

    'agentEditor.err.not_found':          'That agent no longer exists.',
    'agentEditor.err.alias_format':       "alias '{alias}' must be 2-32 chars, a-z 0-9 hyphen, no edge hyphens",
    'agentEditor.err.alias_equals_slug':  'alias must differ from the slug',
    'agentEditor.err.handle_taken':       'the handle "{handle}" is already used by another agent',
    'agentEditor.err.caution_too_long':   'caution must be <= {max} chars; long policies belong in Instructions',
    'agentEditor.err.avatar_type':        'unsupported file type: {type} (png/jpeg/svg/webp/gif only)',
    'agentEditor.err.avatar_type_unknown':'unknown',
    'agentEditor.err.avatar_too_large':   'file too large ({size} MiB); 2 MiB max',
  },
  sv: {
    // ── web-ui AgentsView.vue ──
    'agents.title':                  'Agenter',
    'agents.subtitle':               'Agenterna som nås via @hq. Enbart @hq går till din standardagent; @hq <namn> dirigeras till den agenten. Var och en har egna färdigheter, integrationer och minne.',
    'agents.add':                    'Lägg till agent',

    'agents.nudge.title':            '{name} använder fortfarande plattformens standard.',
    'agents.nudge.sub':              'Skräddarsy agenten efter din verksamhet så att svaren matchar det ditt team faktiskt arbetar med. Vi tar fram ett utkast från din verksamhetsprofil + kanalaktivitet i arbetsytan - du granskar och sparar.',
    'agents.nudge.cta':              'Skräddarsy {name}',

    'agents.empty.title':            'Inga agenter installerade än',
    'agents.empty.body':             'Agenter är personerna som nås via @hq, var och en med egna färdigheter, integrationer och minne. Installera din första för att komma igång.',

    'agents.default':                'Standard',
    'agents.default_tip':            'Standardagent - tar emot meddelanden med enbart @hq',

    'agents.meta.runtime':           'Körmiljö',
    'agents.meta.model':             'Modell',
    'agents.meta.model_default':     'standard',

    'agents.action.set_default':     'Ange som standard',
    'agents.action.disable':         'Inaktivera',
    'agents.action.enable':          'Aktivera',

    'agents.set_default.title':      'Ange {name} som standard?',
    'agents.set_default.body':       'Meddelanden till `@hq` utan persona-token dirigeras till {name}. Bara en agent kan vara standard åt gången.',
    'agents.set_default.confirm':    'Ange som standard',
    'agents.set_default.success':    '{name} är nu din standardagent',
    'agents.set_default.error':      'Det gick inte att ange standard',

    'agents.disable.title':          'Inaktivera {name}?',
    'agents.disable.body':           'Nya `@hq {slug}`-meddelanden går inte att dirigera. Befintliga konversationer kopplade till den här agenten behålls men tar inte emot nya turer.',
    'agents.disable.confirm':        'Inaktivera',
    'agents.disable.success':        'Inaktiverade {name}',
    'agents.disable.error':          'Det gick inte att inaktivera',

    'agents.enable.title':           'Aktivera {name}?',
    'agents.enable.body':            '{name} dirigeras igen via `@hq` och på webben. Dess e-postadress förblir frisläppt (en inaktiverad agents adress kan inte återutfärdas) tills du byter namn på agenten.',
    'agents.enable.confirm':         'Aktivera',
    'agents.enable.success':         'Aktiverade {name}',
    'agents.enable.error':           'Det gick inte att aktivera',

    // ── web-ui AgentEditorView.vue ──
    'agentEditor.title_edit':        'Redigera {name}',
    'agentEditor.title_new':         'Ny agent',
    'agentEditor.agent_noun':        'agent',
    'agentEditor.sections_aria':     'Redigerarsektioner',
    'agentEditor.adv_sections_aria': 'Avancerade sektioner',

    'agentEditor.tab.identity':      'Identitet & dirigering',
    'agentEditor.tab.behavior':      'Beteende',
    'agentEditor.tab.advanced':      'Avancerat',
    'agentEditor.tab.skills':        'Färdigheter',
    'agentEditor.tab.integrations':  'Integrationer',

    'agentEditor.noun.skill':        'färdighet',
    'agentEditor.noun.integration':  'integration',

    'agentEditor.identity.heading':  'Identitet',
    'agentEditor.identity.lede':     'Hur den här agenten namnges och visas för ditt team.',
    'agentEditor.routing.heading':   'Dirigering',
    'agentEditor.routing.lede':      '@hq-handtagen folk använder för att nå den här agenten i Slack.',
    'agentEditor.behavior.heading':  'Beteende',
    'agentEditor.behavior.lede':     'Vad den här agenten gör och hur den pratar.',

    'agentEditor.field.slug':         'Slug',
    'agentEditor.field.display_name': 'Visningsnamn',
    'agentEditor.field.avatar':       'Avatar',
    'agentEditor.field.aliases':      'Alias',
    'agentEditor.field.instructions': 'Instruktioner',
    'agentEditor.field.tone':         'Ton',
    'agentEditor.field.verbosity':    'Utförlighet',
    'agentEditor.field.runtime':      'Körmiljö',
    'agentEditor.field.model':        'Modell',
    'agentEditor.model_locked':       'Kontakta support för att aktivera',
    'agentEditor.field.cautions':     'Varningar',
    'agentEditor.field.languages':    'Språk',

    'agentEditor.hint.slug':          'Dirigeringshandtag (a-z, 0-9, bindestreck)',
    'agentEditor.hint.slug_edit':     'Dirigeringshandtag (a-z, 0-9, bindestreck); byter du namn behålls den gamla sluggen som ett alias',
    'agentEditor.hint.display_name':  'Visas i gränssnittet',
    'agentEditor.hint.description':   'Kort beskrivning som visas i gränssnittet',
    'agentEditor.hint.avatar':        'Visas bredvid den här agentens meddelanden. Ladda upp din egen eller generera en.',
    'agentEditor.hint.aliases':       'Extra handtag utöver sluggen (tryck Enter för att lägga till)',
    'agentEditor.hint.instructions':  'Vad den här agenten gör, omfattning, ton. Tomt = generell hjälpreda.',
    'agentEditor.hint.tone':          'Hur den här agenten pratar; lämna tomt för plattformens standard',
    'agentEditor.hint.verbosity':     'Hur långa svaren ska vara',
    'agentEditor.hint.runtime':       'Vilken agent-SDK som driver den här agenten',
    'agentEditor.hint.model':         'Vilken modell den här agenten körs på; kostnaden skalar med valet',
    'agentEditor.hint.cautions':      'Korta ämnen den här agenten bör undvika (tryck Enter för att lägga till)',
    'agentEditor.hint.languages':     'Det första är standard; resten är språk agenten också får svara på. Klicka på en chip för att göra det till standard.',

    'agentEditor.ph.slug':            'anna',
    'agentEditor.ph.display_name':    'Anna',
    'agentEditor.ph.description':     'CFO - finansrapporter, Stripe-frågor, kostnadsanalys',
    'agentEditor.ph.add_alias':       'lägg till alias',
    'agentEditor.ph.instructions':    'T.ex. Du är Anna, CFO på den här arbetsytan. Din omfattning: finansrapporter, kostnader, resultaträkning, Stripe-frågor. När du får frågor om icke-finansiella ämnen, föreslå att användaren frågar Max eller en annan agent. Var exakt med siffror och hänvisa alltid till källfrågan.',
    'agentEditor.ph.caution':         't.ex. lönedetaljer',

    'agentEditor.tone.formal':        'Formell',
    'agentEditor.tone.friendly':      'Vänlig',
    'agentEditor.tone.direct':        'Rakt på sak',
    'agentEditor.tone.casual':        'Avslappnad',

    'agentEditor.verbosity.concise':  'Kortfattad',
    'agentEditor.verbosity.balanced': 'Balanserad',
    'agentEditor.verbosity.detailed': 'Detaljerad',

    'agentEditor.runtime_note':       'Att byta körmiljö omprovisionerar den här agentens sandbox och återställer modellen till den nya körmiljöns standard.',

    'agentEditor.avatar.uploading':       'Laddar upp…',
    'agentEditor.avatar.replace':         'Ersätt…',
    'agentEditor.avatar.upload':          'Ladda upp…',
    'agentEditor.avatar.generating':      'Genererar…',
    'agentEditor.avatar.generate':        'Generera',
    'agentEditor.avatar.staged_gen_hint': 'Genereras när du skapar agenten.',
    'agentEditor.avatar.staged_hint':     'Förberedd - tillämpas när du skapar agenten.',
    'agentEditor.avatar.updated':         'Uppdaterade avataren för {name}',
    'agentEditor.avatar.upload_failed':   'Uppladdningen av avataren misslyckades',
    'agentEditor.avatar.generated':       'Genererade en ny avatar.',
    'agentEditor.avatar.generate_failed': 'Genereringen av avataren misslyckades',
    'agentEditor.avatar.staged_gen':      'Avataren genereras när du skapar agenten.',

    'agentEditor.tailor.tip':           'Be Claude ta fram ett skräddarsytt utkast med din verksamhetsprofil + kanalaktivitet i arbetsytan. Du granskar + sparar.',
    'agentEditor.tailor.drafting':      'Tar fram utkast…',
    'agentEditor.tailor.cta':           'Skräddarsy för den här arbetsytan',
    'agentEditor.tailor.confirm_title': 'Ersätt de nuvarande instruktionerna?',
    'agentEditor.tailor.confirm_body':  'Claude tar fram ett nytt utkast från din verksamhetsprofil + kanalaktivitet i arbetsytan. Dina nuvarande ändringar tas bort.',
    'agentEditor.tailor.confirm_cta':   'Ta fram nytt',
    'agentEditor.tailor.success':       'Tog fram ett skräddarsytt utkast - granska och spara när du är redo.',
    'agentEditor.tailor.error':         'Skräddarsy-begäran misslyckades',

    'agentEditor.block.slug':         'Lägg till en slug under Identitet & dirigering för att spara.',
    'agentEditor.block.display_name': 'Lägg till ett visningsnamn under Identitet & dirigering för att spara.',

    'agentEditor.save.save_changes':       'Spara ändringar',
    'agentEditor.save.create':             'Skapa agent',
    'agentEditor.save.generating_avatar':  'Genererar avatar…',
    'agentEditor.save.created':            'Skapade agenten {slug}',
    'agentEditor.save.updated':            'Uppdaterade agenten {slug}',
    'agentEditor.save.failed':             'Det gick inte att spara',
    'agentEditor.save.avatar_apply_failed':'Agenten skapades, men avataren kunde inte tillämpas. Ange den från redigeraren.',

    'agentEditor.err.not_found':          'Den agenten finns inte längre.',
    'agentEditor.err.alias_format':       "aliaset '{alias}' måste vara 2-32 tecken, a-z 0-9 bindestreck, inga bindestreck i kanterna",
    'agentEditor.err.alias_equals_slug':  'aliaset måste skilja sig från sluggen',
    'agentEditor.err.handle_taken':       'handtaget "{handle}" används redan av en annan agent',
    'agentEditor.err.caution_too_long':   'en varning måste vara <= {max} tecken; långa policyer hör hemma i Instruktioner',
    'agentEditor.err.avatar_type':        'filtypen stöds inte: {type} (endast png/jpeg/svg/webp/gif)',
    'agentEditor.err.avatar_type_unknown':'okänd',
    'agentEditor.err.avatar_too_large':   'filen är för stor ({size} MiB); max 2 MiB',
  },
};
